import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useDebouncedCallbackPerKey from 'hooks/useDebounceCallbackPerKey';
import Button from 'layout/atoms/Button/Button';
import InputNumber from 'layout/atoms/InputNumber';
import Loader from 'layout/atoms/Loader/Loader';
import Switch from 'layout/atoms/Switch/Switch';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid/TableGrid';
import Grid from 'layout/molecules/Grid/Grid';
import { copyOrderVariantsFromModel, fetchOrderModelVariants, fetchSpecificationModelVariants, selectModelById, selectOrdersCreatorProductionVolume, selectOrdersCreatorRefetchFlags, selectOrdersCreatorStatus, setOrderModelNotReady, setOrderModelReady, updateOrderModelVariant, updateStoreModelVariants, } from 'store/ordersCreatorSlice';
function ProductionVolume({ modelId }) {
    var _a, _b;
    const { t } = useTranslation(['common', 'orders']);
    const dispatch = useDispatch();
    const modelNode = useSelector(selectModelById(modelId));
    const productionVolume = useSelector(selectOrdersCreatorProductionVolume);
    const { modelSpecificationVariants } = productionVolume;
    const { orderModelVariants } = productionVolume;
    const status = useSelector(selectOrdersCreatorStatus).fetchProductionVolume;
    const copyStatus = useSelector(selectOrdersCreatorStatus).other;
    const refetchFlag = useSelector(selectOrdersCreatorRefetchFlags).fetchProductionVolume;
    const modelSpecificationId = (_a = modelNode === null || modelNode === void 0 ? void 0 : modelNode.modelSpecificationNode) === null || _a === void 0 ? void 0 : _a.id;
    const sizes = orderModelVariants === null || orderModelVariants === void 0 ? void 0 : orderModelVariants.sizes;
    const specSizes = modelSpecificationVariants === null || modelSpecificationVariants === void 0 ? void 0 : modelSpecificationVariants.sizes;
    const colors = orderModelVariants === null || orderModelVariants === void 0 ? void 0 : orderModelVariants.colors;
    const colors2 = modelSpecificationVariants === null || modelSpecificationVariants === void 0 ? void 0 : modelSpecificationVariants.colors;
    const modelVariants = orderModelVariants === null || orderModelVariants === void 0 ? void 0 : orderModelVariants.variants;
    const specVariants = (_b = modelSpecificationVariants === null || modelSpecificationVariants === void 0 ? void 0 : modelSpecificationVariants.variants) === null || _b === void 0 ? void 0 : _b.map((variant) => {
        return Object.assign(Object.assign({}, variant), { orderModelColor: variant.color, orderModelSize: variant.size });
    });
    useEffect(() => {
        if (modelSpecificationId && (status === 'idle' || refetchFlag)) {
            dispatch(fetchOrderModelVariants(modelId));
            dispatch(fetchSpecificationModelVariants(modelSpecificationId));
        }
    }, [dispatch, status, refetchFlag, modelId, modelSpecificationId]);
    const sortVariants = (sizes, variantsRow) => {
        const idOrderMap = new Map();
        sizes.forEach((item, index) => {
            idOrderMap.set(item.id, index);
        });
        return variantsRow.sort((a, b) => {
            var _a, _b;
            const indexA = (_a = idOrderMap.get(a.orderModelSize.id)) !== null && _a !== void 0 ? _a : Infinity;
            const indexB = (_b = idOrderMap.get(b.orderModelSize.id)) !== null && _b !== void 0 ? _b : Infinity;
            return indexA - indexB;
        });
    };
    const [debouncedUpdateModelVariants, savingKeys] = useDebouncedCallbackPerKey((name, value) => {
        dispatch(updateOrderModelVariant({ variantId: name, value }));
    }, 1000);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        dispatch(updateStoreModelVariants({ variantId: name, value }));
        debouncedUpdateModelVariants(name, value);
    };
    const handleSwitchChange = () => {
        if (orderModelVariants.modelReady) {
            dispatch(setOrderModelNotReady(modelId));
        }
        else {
            dispatch(setOrderModelReady(modelId));
        }
    };
    if (status === 'idle' || status === 'loading') {
        return (React.createElement(Grid, { gap: 24 },
            React.createElement(Loader, null)));
    }
    return (React.createElement(Grid, { gap: 24, hp: 16, vp: 16 },
        modelVariants && colors && sizes && (React.createElement(TableGrid, { columns: [['120px', '1fr'], ...sizes.map(() => ['100px', '1fr'])] },
            React.createElement(Head, null,
                React.createElement(Row, { head: true },
                    React.createElement(Cell, null, t('common:version')),
                    sizes.map((size) => (React.createElement(Cell, { key: size.id }, size.name))))),
            React.createElement(Body, null, colors.map((color) => (React.createElement(Row, { key: color.id },
                React.createElement(Cell, null, color.name),
                sortVariants(sizes, modelVariants.filter((variant) => variant.orderModelColor.id === color.id)).map((variant) => (React.createElement(Cell, { key: variant.id },
                    React.createElement(InputNumber, { name: variant.id, value: variant.quantity, onChange: handleInputChange, loading: savingKeys.has(variant.id), disabled: orderModelVariants.modelReady })))))))))),
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
            React.createElement(Button, { color: "primary", onClick: () => dispatch(copyOrderVariantsFromModel({ orderModelId: modelId })), disabled: orderModelVariants.modelReady, loading: copyStatus === 'loading' }, t('orders:copy_specification_volume')),
            React.createElement(Switch, { label: orderModelVariants.modelReady ? t('orders:order_model_ready') : t('common:confirm'), value: orderModelVariants.modelReady, onChange: handleSwitchChange })),
        specVariants && colors2 && specSizes && (React.createElement(TableGrid, { columns: [['120px', '1fr'], ...specSizes.map(() => ['100px', '1fr'])] },
            React.createElement(Head, null,
                React.createElement(Row, { head: true },
                    React.createElement(Cell, null, t('common:version')),
                    specSizes.map((size) => (React.createElement(Cell, { key: size.id }, size.sizeName.name))))),
            React.createElement(Body, null, colors2.map((color) => (React.createElement(Row, { key: color.id },
                React.createElement(Cell, null, color.name),
                sortVariants(specSizes, specVariants.filter((v) => v.orderModelColor.id === color.id))
                    .filter((v) => v.isActive)
                    .map((v) => (React.createElement(Cell, { key: v.id }, v.quantity)))))))))));
}
export default memo(ProductionVolume);
